import { useMemo, useState } from 'react';
import { AlertTriangle, Plus } from 'lucide-react';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Modal } from '@/shared/ui/Modal';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast/useToast';
import { formatDate } from '@/shared/utils/format';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { EquipmentItem } from '../api/safetyApi';
import {
  useAddEquipmentMutation,
  useLogEntryMutation,
  useSafetyContent,
  useSafetyEquipment,
} from '../hooks';
import {
  AttestationFooter,
  EquipmentStatusPill,
  PnTag,
  SafetyLoadError,
} from '../components/SafetyBits';
import shared from '../components/safetyShared.module.css';
import styles from './EquipmentLogsPage.module.css';

const CADENCE_LABELS: Record<string, string> = {
  weekly: 'Weekly',
  per_load: 'Per load',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  state: 'State cycle',
};

const CADENCE_ORDER = ['weekly', 'per_load', 'monthly', 'quarterly', 'state'];

export function EquipmentLogsPage() {
  const toast = useToast();
  const contentQuery = useSafetyContent();
  const { data: content } = contentQuery;
  const { data: equipment, isLoading, isError, refetch } = useSafetyEquipment();
  const logEntry = useLogEntryMutation();
  const addEquipment = useAddEquipmentMutation();

  const [logging, setLogging] = useState<EquipmentItem | null>(null);
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [addPackId, setAddPackId] = useState('');

  const groups = useMemo(() => {
    const list = equipment ?? [];
    return CADENCE_ORDER.map((key) => ({
      key,
      label: CADENCE_LABELS[key],
      items: list.filter((e) => e.cadence === key),
    })).filter((g) => g.items.length > 0);
  }, [equipment]);

  const escalated = (equipment ?? []).filter((e) => e.status === 'action_required');

  const openLogModal = (item: EquipmentItem) => {
    const initial: Record<string, string | boolean> = {};
    for (const field of item.fields) {
      if (field.kind === 'boolean') initial[field.key] = false;
      else if (field.kind === 'select') initial[field.key] = field.options?.[0] ?? '';
      else initial[field.key] = '';
    }
    setValues(initial);
    setLogging(item);
  };

  const handleSubmitLog = async () => {
    if (!logging) return;
    try {
      const res = await logEntry.mutateAsync({ equipmentId: logging.id, values });
      if (res.result === 'pass') {
        toast.success(`${logging.label}: logged — pass`);
      } else {
        toast.warning(`${logging.label}: FAIL — ${res.escalationCopy ?? 'follow the escalation'}`);
      }
      setLogging(null);
    } catch {
      toast.error('Could not save the log entry');
    }
  };

  const handleAddEquipment = async () => {
    if (!addPackId) return;
    try {
      await addEquipment.mutateAsync({ packId: addPackId, qty: 1 });
      toast.success('Equipment added — its log starts now');
      setAddOpen(false);
      setAddPackId('');
    } catch {
      toast.error('Could not add the equipment');
    }
  };

  if (isError || contentQuery.isError) {
    return (
      <div className={patterns.page}>
        <PageHeader title="Equipment Logs" />
        <SafetyLoadError
          onRetry={() => {
            refetch();
            contentQuery.refetch();
          }}
        />
      </div>
    );
  }

  if (isLoading || !content) {
    return (
      <div className={patterns.page}>
        <Skeleton height={80} />
        <Skeleton height={300} />
      </div>
    );
  }

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Equipment Logs"
        subtitle="Cadence and pass/fail are computed automatically from each regulation. Positive or failed results trigger the documented escalation."
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus size={14} aria-hidden />}
            onClick={() => setAddOpen(true)}
          >
            Add equipment
          </Button>
        }
      />

      {/* Escalation banners — canonical copy, driven by the state machine */}
      {escalated.map((item) => (
        <div key={item.id} className={shared.escalation}>
          <div className={shared.escalationTitle}>
            <AlertTriangle size={15} aria-hidden />
            {item.label} — {item.escalationLabel}
          </div>
          <p className={shared.escalationBody}>{content.copy.sporeEscalation}</p>
          <div className={shared.escalationActions}>
            <Button variant="primary" size="sm" onClick={() => openLogModal(item)}>
              Log repeat test
            </Button>
          </div>
        </div>
      ))}

      {/* Tables grouped by cadence */}
      {groups.map((group) => (
        <section key={group.key}>
          <div className={patterns.sectionHeader}>
            <span className={patterns.sectionTitle}>{group.label}</span>
          </div>
          <div className={patterns.gridTable}>
            <div className={`${patterns.gridTableHeader} ${styles.logGrid}`}>
              <span>Log</span>
              <span>Last</span>
              <span>Next due</span>
              <span>Status</span>
              <span />
            </div>
            {group.items.map((item) => (
              <div
                key={item.id}
                className={`${patterns.gridTableRow} ${styles.logGrid} ${
                  item.status === 'action_required' ? styles.rowEscalated : ''
                }`}
              >
                <div className={styles.logName}>
                  <span className={patterns.cellName}>{item.label}</span>
                  <PnTag action={item.pnAction} />
                </div>
                <span className={patterns.cellMuted}>
                  {item.lastLoggedAt
                    ? `${formatDate(item.lastLoggedAt)}${
                        item.lastResult ? ` · ${item.lastResult}` : ''
                      }`
                    : 'Never'}
                </span>
                <span className={patterns.cellMuted}>
                  {item.status === 'action_required'
                    ? 'Action required'
                    : item.nextDue
                      ? formatDate(item.nextDue)
                      : '—'}
                </span>
                <span>
                  <EquipmentStatusPill status={item.status} />
                </span>
                <span className={styles.rowAction}>
                  <button className={styles.logLink} onClick={() => openLogModal(item)}>
                    Log result
                  </button>
                </span>
              </div>
            ))}
          </div>
        </section>
      ))}

      {groups.length === 0 && (
        <div className={patterns.panel}>
          <p className={styles.emptyNote}>
            No equipment tracked yet. Add your first item — its regulated cadence attaches
            automatically.
          </p>
        </div>
      )}

      <AttestationFooter text={content.copy.attestation} />

      {/* Log-result modal — capture form per log definition */}
      <Modal
        isOpen={logging !== null}
        onClose={() => setLogging(null)}
        title={logging ? `Log result — ${logging.label}` : ''}
        size="sm"
        footer={
          <div className={styles.modalFooter}>
            <Button variant="secondary" size="sm" onClick={() => setLogging(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmitLog}
              loading={logEntry.isPending}
            >
              Save entry
            </Button>
          </div>
        }
      >
        {logging && (
          <div className={patterns.fieldStack}>
            {logging.fields.map((field) =>
              field.kind === 'boolean' ? (
                <label key={field.key} className={styles.boolField}>
                  <input
                    type="checkbox"
                    checked={values[field.key] === true}
                    onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.checked }))}
                  />
                  {field.label}
                </label>
              ) : field.kind === 'select' ? (
                <Select
                  key={field.key}
                  label={field.label}
                  value={String(values[field.key] ?? '')}
                  onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                  options={(field.options ?? []).map((o) => ({ value: o, label: o }))}
                  fullWidth
                />
              ) : (
                <Input
                  key={field.key}
                  label={field.label}
                  value={String(values[field.key] ?? '')}
                  onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                  fullWidth
                />
              ),
            )}
            <p className={styles.modalHint}>
              Pass/fail is computed automatically. A failed result shows the documented escalation
              step — nothing to decide in the moment.
            </p>
          </div>
        )}
      </Modal>

      {/* Add-equipment modal (JIT setup) */}
      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add equipment"
        size="sm"
        footer={
          <div className={styles.modalFooter}>
            <Button variant="secondary" size="sm" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddEquipment}
              disabled={!addPackId}
              loading={addEquipment.isPending}
            >
              Add
            </Button>
          </div>
        }
      >
        <div className={patterns.fieldStack}>
          <Select
            label="Equipment"
            value={addPackId}
            onChange={(e) => setAddPackId(e.target.value)}
            placeholder="Choose…"
            options={content.equipmentPack.map((p) => ({
              value: p.packId,
              label: `${p.name} — ${p.cadenceLabel}`,
            }))}
            fullWidth
          />
        </div>
      </Modal>
    </div>
  );
}
